import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { filter, first, map, tap } from 'rxjs/operators';
import { zip } from 'rxjs';
import { initializeModule, makeEligibleForInteractions, userNotResponding } from '../actions/routing-status.actions';
import { actualUserLoaded } from '../../users/actions/user.actions';
import { notificationArrived, notificationChannelCreated, subscribeToNotifications } from '../../notifications/actions/notification.actions';
import { routingStatusTopicName } from '../helpers/routing-status-topic.helper';
import { RoutingStatusNotification, RoutingStatusType } from 'common/store/genesys-cloud/routing-status/models/routing-status-notification.model';
import { selectRoutingStatusNotificationTopicName } from '../selectors/routing-status.selectors';
import { ModalService } from 'shared/components/modal-dialog/modal-dialog.service';
import { updatePresence } from 'common/store/genesys-cloud/presence/actions/presence.actions';
import { selectAwayPresence, selectOnQueuePresence } from '../../presence/selectors/presence.selectors';
import { AudioService } from 'common/audio-service/audio.service';

@Injectable()
export class GenesysCloudRoutingStatusEffects {
  readonly modalOptions = {
    headerText: 'genesysRoutingStatusNotification.notice',
    messageBody: 'genesysRoutingStatusNotification.noticeDetails',
    confirmButton: {
      text: 'genesysRoutingStatusNotification.makeEligibleForInteractions',
      clickEvent: () => this.makeEligibleForInteractions()
    },
    cancelButton: {
      text: 'genesysRoutingStatusNotification.goOffQueue',
      clickEvent: () => {}
    },
    resultFunction: () => {},
    closeable: false
  };

  readonly subscribeToRoutingStatusNotifications$ = createEffect(() => zip(
    this.actions$.pipe(ofType(actualUserLoaded)),
    this.actions$.pipe(ofType(notificationChannelCreated)),
    this.actions$.pipe(ofType(initializeModule))
  ).pipe(
    first(),
    map(([{ user }, { channel }]) => subscribeToNotifications({
      userId: user.id,
      channelId: channel.id,
      topics: [routingStatusTopicName(user.id)]
    }))
  ));

  readonly handleRoutingStatusNotification$ = createEffect(() => this.actions$.pipe(
    ofType(notificationArrived),
    concatLatestFrom(() => this.store.select(selectRoutingStatusNotificationTopicName)),
    filter(([{ notification }, topicName]) => notification.topicName === topicName && notification.eventBody?.routingStatus.status === RoutingStatusType.NotResponding),
    map(([{ notification }]) => <RoutingStatusNotification>notification.eventBody),
    map(_routingStatusNotification => userNotResponding())
  ));

  readonly handleUserNotResponding$ = createEffect(() => this.actions$.pipe(
    ofType(userNotResponding),
    concatLatestFrom(() => this.store.select(selectAwayPresence)),
    tap(() => this.modalService.show(this.modalOptions)),
    tap(() => this.audioService.playAcdSetAway()),
    map(([ _, presence ]) => updatePresence({ systemPresenceId: presence.id })
  )));

  readonly makeEligibleForInteractions$ = createEffect(() => this.actions$.pipe(
    ofType(makeEligibleForInteractions),
    concatLatestFrom(() => this.store.select(selectOnQueuePresence)),
    map(([ _, presence ]) => updatePresence({ systemPresenceId: presence.id })
  )));

  constructor(private readonly actions$: Actions,
              private readonly store: Store,
              private readonly modalService: ModalService,
              private readonly audioService: AudioService
  ) {
  }

  makeEligibleForInteractions(): void {
    this.store.dispatch(makeEligibleForInteractions());
  }
}
